import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import MDEditor from "@uiw/react-md-editor";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Save, Upload } from "lucide-react";

export default function AdminBlogEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === "new";
  const [blog, setBlog] = useState({
    title: "", slug: "", category: "General", author: "EQtech Team",
    excerpt: "", content: "# Start writing your post\n\n", featured_image: "",
    tags: [], meta_title: "", meta_description: "", published: true
  });

  useEffect(() => {
    if (!isNew) {
      api.get("/admin/blogs").then(({ data }) => {
        const b = data.find((x) => x.id === id);
        if (b) setBlog({ ...b, tags: b.tags || [] });
      });
    }
  }, [id, isNew]);

  const save = async () => {
    try {
      const payload = { ...blog, tags: Array.isArray(blog.tags) ? blog.tags : String(blog.tags).split(",").map((s) => s.trim()).filter(Boolean) };
      if (isNew) {
        const { data } = await api.post("/admin/blogs", payload);
        toast.success("Blog created");
        navigate(`/admin/blogs`);
      } else {
        await api.patch(`/admin/blogs/${id}`, payload);
        toast.success("Saved");
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const uploadImage = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = data.url.startsWith("http") ? data.url : `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      // Insert markdown image at end of content
      setBlog({ ...blog, content: (blog.content || "") + `\n\n![${f.name}](${url})\n` });
      toast.success("Image inserted");
    } catch { toast.error("Upload failed"); }
    e.target.value = "";
  };

  const uploadFeatured = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = data.url.startsWith("http") ? data.url : `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      setBlog({ ...blog, featured_image: url });
      toast.success("Uploaded");
    } catch { toast.error("Upload failed"); }
    e.target.value = "";
  };

  const cls = "w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:border-sky-500";

  return (
    <div className="p-8" data-testid="admin-blog-editor">
      <div className="flex items-center justify-between mb-6">
        <div>
          <Link to="/admin/blogs" className="inline-flex items-center gap-1 text-sm text-neutral-500 hover:text-black"><ArrowLeft className="w-4 h-4" /> Back to blogs</Link>
          <h1 className="font-display font-black text-3xl mt-2" data-testid="editor-title">{isNew ? "New Blog Post" : "Edit Blog Post"}</h1>
        </div>
        <button onClick={save} className="btn-primary-eq text-sm" data-testid="editor-save"><Save className="w-4 h-4" /> Save</button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <input required placeholder="Post title" value={blog.title} onChange={(e) => setBlog({ ...blog, title: e.target.value })}
                 className={`${cls} text-lg font-semibold`} data-testid="editor-title-input" />
          <input required placeholder="Slug (url-friendly)" value={blog.slug} onChange={(e) => setBlog({ ...blog, slug: e.target.value })} className={cls} data-testid="editor-slug" />
          <textarea rows={2} placeholder="Short excerpt (used in listings)" value={blog.excerpt} onChange={(e) => setBlog({ ...blog, excerpt: e.target.value })} className={cls} data-testid="editor-excerpt" />

          <div className="flex items-center justify-between">
            <label className="text-xs font-semibold text-neutral-600">Content</label>
            <label className="inline-flex items-center gap-1 text-xs cursor-pointer text-sky-600 hover:text-sky-700">
              <Upload className="w-3 h-3" /> Insert image
              <input type="file" accept="image/*" hidden onChange={uploadImage} data-testid="editor-image-upload" />
            </label>
          </div>
          <div data-color-mode="light" data-testid="editor-md">
            <MDEditor value={blog.content} onChange={(v) => setBlog({ ...blog, content: v || "" })}
                      height={520} preview="live" visibleDragbar={false} />
          </div>
        </div>

        <aside className="space-y-4">
          <div className="bg-white p-4 rounded-2xl border border-neutral-200">
            <div className="text-xs font-semibold text-neutral-600">Featured image</div>
            {blog.featured_image && (
              <img src={blog.featured_image} alt="" className="mt-2 w-full aspect-video object-cover rounded-lg" />
            )}
            <input value={blog.featured_image || ""} onChange={(e) => setBlog({ ...blog, featured_image: e.target.value })}
                   placeholder="Image URL" className={`${cls} mt-2 text-xs`} />
            <label className="btn-outline-eq w-full justify-center mt-2 text-xs cursor-pointer py-2">
              Upload <input type="file" accept="image/*" hidden onChange={uploadFeatured} data-testid="editor-featured-upload" />
            </label>
          </div>

          <div className="bg-white p-4 rounded-2xl border border-neutral-200 space-y-3">
            <div>
              <label className="text-xs font-semibold text-neutral-600">Category</label>
              <input value={blog.category} onChange={(e) => setBlog({ ...blog, category: e.target.value })} className={cls} />
            </div>
            <div>
              <label className="text-xs font-semibold text-neutral-600">Author</label>
              <input value={blog.author} onChange={(e) => setBlog({ ...blog, author: e.target.value })} className={cls} />
            </div>
            <div>
              <label className="text-xs font-semibold text-neutral-600">Tags (comma-separated)</label>
              <input value={Array.isArray(blog.tags) ? blog.tags.join(", ") : blog.tags}
                     onChange={(e) => setBlog({ ...blog, tags: e.target.value })} className={cls} />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!blog.published} onChange={(e) => setBlog({ ...blog, published: e.target.checked })} />
              Published
            </label>
          </div>

          <div className="bg-white p-4 rounded-2xl border border-neutral-200 space-y-3">
            <div className="text-xs font-semibold text-neutral-600">SEO</div>
            <input value={blog.meta_title || ""} placeholder="Meta title" onChange={(e) => setBlog({ ...blog, meta_title: e.target.value })} className={cls} />
            <textarea rows={2} value={blog.meta_description || ""} placeholder="Meta description" onChange={(e) => setBlog({ ...blog, meta_description: e.target.value })} className={cls} />
          </div>
        </aside>
      </div>
    </div>
  );
}
